/** Compound feedback resource: open submission plus vendor-only triage verbs. */
import type { GatewayRouteReturnByName } from "@productbrain/kernel-client";
import { McpServer } from '@modelcontextprotocol/sdk/server/mcp.js';
import { z } from 'zod/v3';
import { getAgentSessionId, kernelCall, requireVendorTriageAccess, runWithToolContext } from '../client.js';
import { thinWrapper, parseOrFail, failureResult, success, textContent, type ToolResult } from '../envelope.js';
import { dispatchDiscriminated, type ActionParamSpec } from '../compound-tool.js';
import { PRODUCT_FEEDBACK_CATEGORIES, PRODUCT_FEEDBACK_STATUSES } from '../lib/productFeedbackConstants.js';

const actions = ['submit', 'list', 'note', 'group', 'status'] as const;
type FeedbackAction = (typeof actions)[number];
const category = z.enum(PRODUCT_FEEDBACK_CATEGORIES);
const status = z.enum(PRODUCT_FEEDBACK_STATUSES);

/**
 * The gateway (`validateArgLengths`, TEN-1146) rejects any string argument over
 * 10,240 UTF-8 BYTES. A characters-only client cap (10,000 chars) let multibyte text
 * pass the client and 4xx at the gateway (Codex P2, PR #372) — so the client contract
 * enforces the byte limit too, with an error that names bytes.
 */
export const GATEWAY_MAX_STRING_BYTES = 10_240;
const utf8Bytes = (value: string): number => new TextEncoder().encode(value).length;
const fitsGatewayBytes = (value: string): boolean => utf8Bytes(value) <= GATEWAY_MAX_STRING_BYTES;
const byteLimitMessage = (field: string) => ({
  message: `${field} exceeds the gateway limit of ${GATEWAY_MAX_STRING_BYTES.toLocaleString('en-US')} UTF-8 bytes — multibyte characters can hit the byte limit well below the 10,000-character cap; shorten the text.`,
});

/** Display cap for list rows: a 100-row page of 10,000-char messages approaches ~2MB (Codex P2, PR #372). */
const MESSAGE_DISPLAY_LIMIT = 1_000;
/** `full: true` lifts truncation only for a small targeted fetch, never a whole page. */
const FULL_MESSAGE_MAX_LIMIT = 5;

export const feedbackSchema = z.object({
  action: z.enum(actions).describe('submit requires message; list accepts filters; note requires feedbackId+note; group requires feedbackIds+groupId (null clears); status requires feedbackId+status.'),
  message: z.string().max(10_000).optional().describe('Required for submit: the product feedback text (max 10,000 chars and 10,240 UTF-8 bytes).'),
  category: category.optional().describe('Submit/list category; submit defaults to other.'),
  command: z.string().max(1_000).optional().describe('Optional submit command context; argument values are scrubbed server-side.'),
  route: z.string().max(500).optional().describe('Optional submit route context.'),
  client: z.string().max(100).optional().describe('Optional submit client label.'),
  feedbackId: z.string().max(200).optional().describe('Required for note/status: target feedback ID.'),
  feedbackIds: z.array(z.string().max(200)).max(100).optional().describe('Required for group: 1–100 target feedback IDs.'),
  note: z.string().max(4_000).optional().describe('Required for note: replacement triage note (max 10,240 UTF-8 bytes).'),
  status: status.optional().describe('List filter or required status action value.'),
  groupId: z.string().max(200).nullable().optional().describe('Group filter, or required group destination; null explicitly clears grouping.'),
  workspaceId: z.string().max(200).optional().describe('Vendor-only list filter; omitted means all workspaces.'),
  since: z.number().optional().describe('Vendor list lower createdAt window bound (inclusive) in epoch milliseconds.'),
  before: z.number().optional().describe('Vendor list upper createdAt window bound (exclusive) in epoch milliseconds. A time-window FILTER, not the pager — use cursor to page.'),
  limit: z.number().int().min(1).max(100).optional().describe('Vendor list row limit, default 50, maximum 100.'),
  cursor: z.string().max(2_000).optional().describe('Opaque pagination cursor from a previous list response (continueCursor); omit for the first page.'),
  full: z.boolean().optional().describe(`List only: return untruncated messages; allowed only when limit <= ${FULL_MESSAGE_MAX_LIMIT}.`),
});

const union = z.discriminatedUnion('action', [
  z.object({ action: z.literal('submit'), message: z.string().min(1).max(10_000).refine(fitsGatewayBytes, byteLimitMessage('message')), category: category.optional(), command: z.string().max(1_000).optional(), route: z.string().max(500).optional(), client: z.string().max(100).optional() }),
  z.object({ action: z.literal('list'), status: status.optional(), category: category.optional(), groupId: z.string().max(200).optional(), workspaceId: z.string().max(200).optional(), since: z.number().optional(), before: z.number().optional(), limit: z.number().int().min(1).max(100).optional(), cursor: z.string().max(2_000).optional(), full: z.boolean().optional() }),
  z.object({ action: z.literal('note'), feedbackId: z.string().min(1).max(200), note: z.string().max(4_000).refine(fitsGatewayBytes, byteLimitMessage('note')) }),
  z.object({ action: z.literal('group'), feedbackIds: z.array(z.string().min(1).max(200)).min(1).max(100), groupId: z.string().max(200).nullable() }),
  z.object({ action: z.literal('status'), feedbackId: z.string().min(1).max(200), status }),
]);

const specs: Record<FeedbackAction, ActionParamSpec> = {
  submit: { params: ['message', 'category', 'command', 'route', 'client'], description: 'message is required; category defaults to other.' },
  list: { params: ['status', 'category', 'groupId', 'workspaceId', 'since', 'before', 'limit', 'cursor', 'full'], description: 'Vendor queue filters are optional; status defaults to new; cursor pages, full lifts message truncation for limit <= 5.' },
  note: { params: ['feedbackId', 'note'], description: 'feedbackId and note are required.' },
  group: { params: ['feedbackIds', 'groupId'], description: 'feedbackIds and groupId are required; null clears a group.' },
  status: { params: ['feedbackId', 'status'], description: 'feedbackId and status are required.' },
};

function result(action: FeedbackAction, data: unknown): ToolResult {
  const summary = action === 'submit' ? 'Feedback submitted.' : `Feedback ${action} complete.`;
  return { content: textContent(`${summary}\n${JSON.stringify(data, null, 2)}`), structuredContent: success(summary, { action, result: data }) };
}

type GroupRowResult = { feedbackId?: string; ok?: boolean; error?: string };

/**
 * The server applies group rows independently and reports per-row `{ok, error?}` results —
 * a blanket "Feedback group complete." hid partial failure (Codex, PR #372 round 5). The
 * summary is derived from the results: applied/failed counts with each failed row's reason,
 * and an MCP failure envelope when EVERY row failed. structuredContent always carries the
 * full per-row results.
 */
function groupResult(data: unknown): ToolResult {
  const rows = (data as { results?: unknown } | null)?.results;
  if (!Array.isArray(rows)) return result('group', data); // Tolerate a non-per-row kernel shape.
  const results = rows as GroupRowResult[];
  const failed = results.filter((row) => row?.ok !== true);
  const applied = results.length - failed.length;
  const failedLines = failed.map((row) => `- ${row.feedbackId ?? 'unknown'}: ${row.error ?? 'unknown error'}`);
  if (results.length > 0 && applied === 0) {
    const message = `Feedback group failed for all ${failed.length} row(s).`;
    return failureResult(
      `${message}\n${failedLines.join('\n')}`,
      'GROUP_FAILED',
      message,
      'Check the per-row errors in diagnostics.results, fix the target IDs or group destination, and retry.',
      undefined,
      { action: 'group', results },
    );
  }
  const summary = failed.length === 0
    ? `Feedback group: ${applied} applied.`
    : `Feedback group: ${applied} applied, ${failed.length} failed.`;
  const text = failed.length === 0
    ? `${summary}\n${JSON.stringify(data, null, 2)}`
    : `${summary}\nFailed rows:\n${failedLines.join('\n')}\n${JSON.stringify(data, null, 2)}`;
  return { content: textContent(text), structuredContent: success(summary, { action: 'group', result: data }) };
}

type FeedbackListPage = { items: unknown[]; continueCursor: string | null; isDone: boolean };

/** Tolerate a pre-cursor kernel that still answers with plain rows. */
function normalizeListPage(raw: unknown): FeedbackListPage {
  if (Array.isArray(raw)) return { items: raw, continueCursor: null, isDone: true };
  const page = (raw ?? {}) as Partial<FeedbackListPage>;
  return {
    items: Array.isArray(page.items) ? page.items : [],
    continueCursor: typeof page.continueCursor === 'string' ? page.continueCursor : null,
    isDone: page.isDone !== false,
  };
}

/**
 * Truncate a row's message for DISPLAY (Codex P2, PR #372): a 100-row page of 10,000-char
 * messages approaches ~2MB. `structuredContent` carries the SAME truncated rows — full
 * bodies are never duplicated; `full: true` (limit <= 5) or a cursor-paged smaller fetch
 * is the path to a whole message.
 */
function truncateListItems(items: unknown[], full: boolean): unknown[] {
  if (full) return items;
  return items.map((item) => {
    if (item === null || typeof item !== 'object') return item;
    const row = item as Record<string, unknown>;
    if (typeof row.message !== 'string' || row.message.length <= MESSAGE_DISPLAY_LIMIT) return item;
    return {
      ...row,
      message: `${row.message.slice(0, MESSAGE_DISPLAY_LIMIT)}… (truncated, ${row.message.length} chars total)`,
    };
  });
}

function requireTriageSessionId(): string {
  // Triage-specific gate, NOT requireWriteAccess: the server's validateVendorTriageSession
  // requires an active session and a readwrite key but deliberately omits requireOriented —
  // the session-start queue hint points at `feedback action=list` before any orient call.
  requireVendorTriageAccess();
  const sessionId = getAgentSessionId();
  if (!sessionId) throw new Error('Agent session required for feedback triage.');
  return sessionId;
}

export function registerFeedbackTool(server: McpServer): void {
  const handlers: Record<FeedbackAction, (data: any) => Promise<ToolResult>> = {
    submit: async (data) => {
      const sessionId = getAgentSessionId();
      return result('submit', await kernelCall<GatewayRouteReturnByName<"feedback.submit">>('feedback.submit', {
        message: data.message,
        category: data.category ?? 'other',
        ...(sessionId ? { sessionId } : {}),
        context: { command: data.command, route: data.route, client: data.client ?? 'mcp' },
      }));
    },
    list: async ({ action: _action, full, cursor, ...data }) => {
      const wantFull = full === true;
      if (wantFull && (data.limit ?? 50) > FULL_MESSAGE_MAX_LIMIT) {
        throw new Error(`full=true returns untruncated messages and is only allowed with limit <= ${FULL_MESSAGE_MAX_LIMIT}; re-fetch the target rows with a smaller limit (page with cursor).`);
      }
      // cursor ?? null: always request the paged shape so continueCursor/isDone are served
      // even on the first page — `before` stays a time-window filter, never the pager.
      const page = normalizeListPage(await kernelCall('feedback.list', {
        ...data, cursor: cursor ?? null, sessionId: requireTriageSessionId(),
      }));
      return result('list', { ...page, items: truncateListItems(page.items, wantFull) });
    },
    note: async ({ action: _action, ...data }) => result('note', await kernelCall('feedback.note', { ...data, sessionId: requireTriageSessionId() })),
    group: async ({ action: _action, ...data }) => groupResult(await kernelCall<GatewayRouteReturnByName<"feedback.group">>('feedback.group', { ...data, sessionId: requireTriageSessionId() })),
    status: async ({ action: _action, ...data }) => result('status', await kernelCall('feedback.status', { ...data, sessionId: requireTriageSessionId() })),
  };

  server.registerTool('feedback', {
    title: 'Feedback — Submit & Triage',
    description: 'Submit Product Brain feedback from any agent, including with a read-only key and no session. Vendor operators can also list, note, group, and change queue status.',
    inputSchema: feedbackSchema,
    annotations: { readOnlyHint: false, destructiveHint: true, openWorldHint: false },
  }, thinWrapper(async (args) => {
    const parsed = parseOrFail(feedbackSchema, args);
    if (!parsed.ok) return parsed.result;
    return runWithToolContext({ tool: 'feedback', action: parsed.data.action }, () =>
      dispatchDiscriminated('feedback', union, parsed.data, specs, handlers));
  }));
}
